import { useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@moneyskyline/ui';
import { useApplyToSimilar } from '@/api/hooks/useApplyToSimilar';
import { useBatchUndo } from '@/api/hooks/useBatchUndo';
import { useSimilarPreview, type SimilarPreviewData, type SimilarPreviewScope } from '@/api/hooks/useSimilarPreview';
import { showUndoToast } from '@/components/UndoToast';

export interface SimilarListProps {
  merchantName: string;
  lineItemId?: string;
  lineItemVersion?: number;
  categoryId?: string;
  taxonomyVersion?: string;
  businessState?: 'business' | 'personal';
}

function scopeFor(kind: 'all_time' | 'date_range', dateFrom: string, dateTo: string): SimilarPreviewScope | null {
  return kind === 'all_time'
    ? { kind: 'all_time' }
    : dateFrom !== '' && dateTo !== ''
      ? { kind: 'date_range', date_from: dateFrom, date_to: dateTo }
      : null;
}

export function SimilarList({
  merchantName,
  lineItemId,
  lineItemVersion,
  categoryId,
  taxonomyVersion,
  businessState,
}: SimilarListProps) {
  const preview = useSimilarPreview();
  const apply = useApplyToSimilar();
  const batchUndo = useBatchUndo();
  const openButtonRef = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [scopeKind, setScopeKind] = useState<'all_time' | 'date_range'>('all_time');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [futureCondition, setFutureCondition] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewData, setPreviewData] = useState<SimilarPreviewData>();
  const [previewCoverage, setPreviewCoverage] = useState<'complete' | 'partial' | undefined>();
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [commitError, setCommitError] = useState(false);
  const [expired, setExpired] = useState(false);
  const previewGeneration = useRef(0);

  const canPreview = Boolean(lineItemId && lineItemVersion && categoryId && taxonomyVersion);
  const previewBlocker = !lineItemId || !lineItemVersion
    ? 'Select a line item before similar items can be previewed.'
    : !categoryId
      ? 'Categorize this item before similar items can be previewed.'
      : !taxonomyVersion
        ? 'Category taxonomy is not available yet.'
        : null;

  function requestPreview(scope: SimilarPreviewScope) {
    if (!lineItemId || !lineItemVersion || !categoryId || !taxonomyVersion) return;
    const generation = previewGeneration.current + 1;
    previewGeneration.current = generation;
    setPreviewData(undefined);
    setPreviewCoverage(undefined);
    setPreviewError(false);
    setExpired(false);
    setPreviewing(true);
    preview.mutate(
      {
        lineItemId,
        expectedVersion: lineItemVersion,
        changes: {
          category: { category_id: categoryId, taxonomy_version: taxonomyVersion },
          ...(businessState ? { business_state: businessState } : {}),
        },
        scope,
      },
      {
        onSuccess: (result) => {
          if (previewGeneration.current !== generation) return;
          setPreviewData(result.data);
          setPreviewCoverage(result.meta.coverage);
          setSelectedIds(new Set(result.data.matches.map((match) => match.line_item_id)));
          setExpired(Date.parse(result.data.expires_at) <= Date.now());
          setPreviewing(false);
        },
        onError: () => {
          if (previewGeneration.current !== generation) return;
          setPreviewing(false);
          setPreviewError(true);
        },
      },
    );
  }

  function openReview() {
    setOpen(true);
    setScopeKind('all_time');
    setDateFrom('');
    setDateTo('');
    setFutureCondition(true);
    setCommitError(false);
    requestPreview({ kind: 'all_time' });
  }

  function closeReview() {
    setOpen(false);
    window.requestAnimationFrame(() => openButtonRef.current?.focus());
  }

  function previewCurrentScope() {
    const scope = scopeFor(scopeKind, dateFrom, dateTo);
    if (scope) requestPreview(scope);
  }

  function invalidatePreview() {
    previewGeneration.current += 1;
    setPreviewData(undefined);
    setPreviewCoverage(undefined);
    setExpired(false);
    setPreviewing(false);
    setCommitError(false);
  }

  function changeScopeKind(next: 'all_time' | 'date_range') {
    setScopeKind(next);
    invalidatePreview();
    if (next === 'all_time') requestPreview({ kind: 'all_time' });
  }

  function toggleMatch(lineItemIdToToggle: string) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(lineItemIdToToggle)) next.delete(lineItemIdToToggle);
      else next.add(lineItemIdToToggle);
      return next;
    });
  }

  function commitSelection() {
    if (!previewData || previewData.matches.length === 0) return;
    if (Date.parse(previewData.expires_at) <= Date.now()) {
      setExpired(true);
      return;
    }
    setCommitError(false);
    apply.mutate(
      {
        lineItemId: previewData.source_line_item_id,
        previewId: previewData.preview_id,
        acceptIds: Array.from(selectedIds),
        createFutureCondition: futureCondition,
      },
      {
        onSuccess: (result) => {
          const count = result.data.applied_count;
          const noun = count === 1 ? 'item' : 'items';
          showUndoToast({
            message: count === 0 ? `Saved ${merchantName}; no selected items needed an update.` : `Saved ${count} selected ${merchantName} ${noun}.`,
            batchId: result.data.batch_id,
            onUndo: (batchId) => batchUndo.undo.mutate(batchId),
          });
          closeReview();
        },
        onError: () => setCommitError(true),
      },
    );
  }

  const selectedCount = selectedIds.size;

  return (
    <div className="mt-3 text-xs text-muted-foreground" data-testid="similar-list">
      <div className="font-semibold text-foreground">Similar · {merchantName}</div>
      <span ref={openButtonRef} className="inline-flex">
        <Button
          type="button"
          variant="link"
          size="sm"
          className="mt-1 h-auto min-h-11 px-0"
          disabled={!canPreview}
          onClick={openReview}
        >
          Review similar items
        </Button>
      </span>
      {!canPreview && previewBlocker && <p className="mt-1">{previewBlocker}</p>}

      <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : closeReview())}>
        <DialogContent data-testid="similar-dialog" className="max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Similar · {merchantName}</DialogTitle>
            <DialogDescription>Review the server preview, choose the historical items to update, and decide separately about future charges.</DialogDescription>
          </DialogHeader>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Historical scope</legend>
            <label className="flex min-h-11 items-center gap-2">
              <input type="radio" name="similar-scope" value="all_time" checked={scopeKind === 'all_time'} onChange={() => changeScopeKind('all_time')} />
              All time
            </label>
            <label className="flex min-h-11 items-center gap-2">
              <input type="radio" name="similar-scope" value="date_range" checked={scopeKind === 'date_range'} onChange={() => changeScopeKind('date_range')} />
              Date range
            </label>
            {scopeKind === 'date_range' && (
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="grid gap-1 text-sm">From date<input aria-label="From date" type="date" value={dateFrom} onChange={(event) => { setDateFrom(event.target.value); invalidatePreview(); }} /></label>
                <label className="grid gap-1 text-sm">To date<input aria-label="To date" type="date" value={dateTo} onChange={(event) => { setDateTo(event.target.value); invalidatePreview(); }} /></label>
              </div>
            )}
            {scopeKind === 'date_range' && <Button type="button" variant="outline" onClick={previewCurrentScope} disabled={!dateFrom || !dateTo || previewing}>Preview matches</Button>}
          </fieldset>

          {previewing && <p role="status">Loading similar preview…</p>}
          {previewError && (
            <div className="space-y-2" role="alert">
              <p>Similar items could not be previewed. Try again.</p>
              <Button type="button" variant="outline" onClick={previewCurrentScope}>Try preview again</Button>
            </div>
          )}
          {previewData && !previewError && (
            <div className="space-y-3">
              {expired && (
                <div className="space-y-2" role="alert">
                  <p>This preview expired. Preview again to continue.</p>
                  <Button type="button" variant="outline" onClick={previewCurrentScope}>Preview again</Button>
                </div>
              )}
              {!expired && (
                <>
                  <p data-testid="similar-count">
                    {previewCoverage === 'partial'
                      ? `Known matches: ${previewData.matches.length}; total unavailable.`
                      : previewData.matches.length < previewData.match_count
                        ? `Showing ${previewData.matches.length} of ${previewData.match_count} matching items`
                        : `${previewData.match_count} matching ${previewData.match_count === 1 ? 'item' : 'items'}`}
                  </p>
                  <p>Excluded: {previewData.excluded.user_override} edited · {previewData.excluded.split} split · {previewData.excluded.not_editable} not editable.</p>
                  <div className="max-h-56 space-y-2 overflow-auto" aria-label="Similar matches">
                    {previewData.matches.map((match) => (
                      <label key={match.line_item_id} className="flex min-h-11 items-center gap-2 rounded-md border border-border p-2">
                        <input type="checkbox" checked={selectedIds.has(match.line_item_id)} onChange={() => toggleMatch(match.line_item_id)} aria-label={`Select ${match.merchant ?? 'similar item'} ${match.posted_day}`} />
                        <span className="flex-1">{match.merchant ?? 'Merchant unavailable'} · {match.posted_day}</span>
                        <span>{match.amount} {match.currency}</span>
                      </label>
                    ))}
                  </div>
                  <label className="flex min-h-11 items-center gap-2 border-t border-border pt-2">
                    <input type="checkbox" checked={futureCondition} onChange={(event) => setFutureCondition(event.target.checked)} aria-label="Save a future condition" />
                    Save a future condition for {merchantName}
                  </label>
                  {commitError && <p role="alert">We couldn’t confirm whether the similar items were updated. Refresh before trying again.</p>}
                </>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeReview}>Keep this change only</Button>
            <Button type="button" onClick={commitSelection} disabled={expired || !previewData || selectedCount === 0 || apply.isPending}>
              {apply.isPending ? 'Saving…' : 'Apply to selected'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SimilarList;
